/**
 * Pure helpers for the execution "Children" tab tree view.
 *
 * The Children tab loads a flat set of descendant executions (the `descendant_of`
 * recursive filter, or `parent_execution_id` for the direct-children scope). To
 * render that set as an expandable/collapsible TREE we assemble a parent/child
 * SPAWN forest from each row's `parent_execution_id`.
 *
 * This is deliberately the `parent_execution_id` relationship (a strict tree —
 * every execution has at most one parent), which is the relationship kind the
 * product calls the "parent/child tree" (as opposed to the dependency GRAPH,
 * which the dependency-visualization renders and which each row also surfaces via
 * its Relation kind chip). Keeping the two surfaces on their respective models is
 * intentional: the tree conveys spawn depth, the chip conveys dependency kind.
 *
 * Everything here is pure and DB-free so it is unit-testable without a running
 * workspace (there is no local dev backend; jest is the CI-faithful check).
 */

export interface ExecutionRowLike {
    id: string;
    parent_execution_id?: string | null;
}

export interface ExecutionTreeNode<T extends ExecutionRowLike> {
    execution: T;
    children: ExecutionTreeNode<T>[];
    /** 0 for a root of the returned forest, +1 per level below it. */
    depth: number;
}

/** A single row in the flattened, depth-annotated render order. */
export interface FlatTreeRow<T extends ExecutionRowLike> {
    execution: T;
    depth: number;
    hasChildren: boolean;
    /** Total descendants under this node (all levels), for a count badge. */
    descendantCount: number;
}

/**
 * Assemble a parent/child spawn forest from a flat list of execution rows.
 *
 * Rules:
 * - Edges follow `parent_execution_id`. A child is attached under its parent iff
 *   that parent id is itself present in `rows`.
 * - A row whose `parent_execution_id` is missing, null, self-referential, or not
 *   present in the supplied set becomes a top-level root of the forest. (In the
 *   direct-children scope every parent is the viewed execution — which is not in
 *   the set — so all rows are roots, i.e. a flat single level. In the
 *   all-descendants scope, spawn structure is reconstructed and any
 *   dependency-only descendant whose structural parent is outside the loaded set
 *   surfaces as a root.)
 * - Sibling order preserves input order (callers pre-sort via the table order).
 * - Cycle-safe: a node is never nested under any of its own descendants; if a
 *   cycle is detected the offending edge is dropped and the node is treated as a
 *   root, so the function always terminates and every input row appears exactly
 *   once.
 */
export function buildExecutionForest<T extends ExecutionRowLike>(
    rows: readonly T[]
): ExecutionTreeNode<T>[] {
    const nodeById = new Map<string, ExecutionTreeNode<T>>();
    // First pass: create a node per row (last-wins on duplicate ids).
    for (const execution of rows) {
        nodeById.set(execution.id, { execution, children: [], depth: 0 });
    }

    const roots: ExecutionTreeNode<T>[] = [];

    const isAncestor = (
        candidateAncestorId: string,
        node: ExecutionTreeNode<T>
    ): boolean => {
        // Walk up from `node` via already-linked parents to detect a cycle before
        // we attach it. Bounded by the current chain length.
        let current: string | null | undefined = node.execution.parent_execution_id;
        const guard = new Set<string>();
        while (current && !guard.has(current)) {
            if (current === candidateAncestorId) {
                return true;
            }
            guard.add(current);
            current = nodeById.get(current)?.execution.parent_execution_id;
        }
        return false;
    };

    // Second pass: link children to parents (or promote to root).
    for (const execution of rows) {
        const node = nodeById.get(execution.id);
        if (!node) {
            continue;
        }
        const parentId = execution.parent_execution_id;
        const parent = parentId ? nodeById.get(parentId) : undefined;
        if (
            parent &&
            parentId !== execution.id &&
            !isAncestor(execution.id, parent)
        ) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
    }

    // Third pass: stamp depth top-down (iterative to avoid deep recursion).
    const stack: ExecutionTreeNode<T>[] = roots.map((r) => {
        r.depth = 0;
        return r;
    });
    while (stack.length) {
        const node = stack.pop() as ExecutionTreeNode<T>;
        for (const child of node.children) {
            child.depth = node.depth + 1;
            stack.push(child);
        }
    }

    return roots;
}

/** Count all descendants (every level) under a node. */
export function countDescendants<T extends ExecutionRowLike>(
    node: ExecutionTreeNode<T>
): number {
    let total = 0;
    const stack = [...node.children];
    while (stack.length) {
        const current = stack.pop() as ExecutionTreeNode<T>;
        total += 1;
        for (const child of current.children) {
            stack.push(child);
        }
    }
    return total;
}

/**
 * Flatten a forest into depth-annotated rows in display order (pre-order DFS,
 * sibling order preserved), including a node's children only when the node id is
 * in `expandedIds`. Collapsed subtrees are omitted. This is what the tree view
 * maps over to render rows with indentation + an expand/collapse control.
 */
export function flattenForest<T extends ExecutionRowLike>(
    roots: readonly ExecutionTreeNode<T>[],
    expandedIds: ReadonlySet<string>
): FlatTreeRow<T>[] {
    const out: FlatTreeRow<T>[] = [];

    const visit = (node: ExecutionTreeNode<T>) => {
        const hasChildren = node.children.length > 0;
        out.push({
            execution: node.execution,
            depth: node.depth,
            hasChildren,
            descendantCount: countDescendants(node),
        });
        if (hasChildren && expandedIds.has(node.execution.id)) {
            for (const child of node.children) {
                visit(child);
            }
        }
    };

    for (const root of roots) {
        visit(root);
    }
    return out;
}

/** Collect every node id in the forest (for an expand-all affordance). */
export function collectAllIds<T extends ExecutionRowLike>(
    roots: readonly ExecutionTreeNode<T>[]
): Set<string> {
    const ids = new Set<string>();
    const stack = [...roots];
    while (stack.length) {
        const node = stack.pop() as ExecutionTreeNode<T>;
        ids.add(node.execution.id);
        for (const child of node.children) {
            stack.push(child);
        }
    }
    return ids;
}
